import { useMemo, useCallback } from "react";
import {
  ArrowRightIcon,
  ArrowsDownUpIcon,
  ArrowsLeftRightIcon,
  CaretDownIcon,
  SlidersHorizontalIcon,
} from "@phosphor-icons/react";
import { useTranslations } from "use-intl";
import type {
  FlightFormLocation,
  FlightsSortValue,
  TFlightFormValues,
} from "@/app/lib/types/types";
import { TripType, CabinClass, SortOrder } from "@/app/lib/types/enums";
import { useFlightSearchDataContext } from "@/app/lib/context/FlightSearchDataContext";
import { FilterButton, FilterButtonSkeleton } from "../FilterButton";
import { HeaderSearchButton } from "../HeaderSearchButton";
import { formatHeaderDate } from "@/app/lib/utils/dates";
import { CalendarCards } from "../CalendarCards";
import clsx from "clsx";
import { useMainScroll } from "@/app/lib/hooks/useMainScroll";
import { FlightsListDrawerComponent } from "./types";
import type { IFlightFilters } from "@/app/lib/types/interfaces";
import { times } from "lodash-es";

const sortOptionsLabels = {
  [SortOrder.ASC]: "flights.cheapFirst",
  [SortOrder.DESC]: "flights.expensiveFirst",
};

const cabinClassLabels = {
  [CabinClass.ANY]: "flights.cabinClass.anyClass",
  [CabinClass.ECONOMY]: "flights.cabinClass.economy",
  [CabinClass.BUSINESS]: "flights.cabinClass.business",
  [CabinClass.PREMIUM_ECONOMY]: "flights.cabinClass.premiumEconomy",
  [CabinClass.FIRST_CLASS]: "flights.cabinClass.firstClass",
};

function formatLocation(location: FlightFormLocation) {
  return [
    location.code,
    "cityName" in location ? location.cityName : location.name,
  ]
    .filter(Boolean)
    .join(" ");
}

type Props = {
  selectedSortValue: FlightsSortValue;
  isLoading?: boolean;
  activeFilters: Record<keyof IFlightFilters, boolean> | null;
  onFilterButtonClicked: (drawerComponent: FlightsListDrawerComponent) => void;
  onSearchDrawerOpen: () => void;
};

function Header({
  selectedSortValue,
  isLoading,
  activeFilters,
  onSearchDrawerOpen,
  onFilterButtonClicked,
}: Props) {
  const t = useTranslations();
  const { y: scrollY } = useMainScroll();
  const { searchData, updateSearchData } = useFlightSearchDataContext();

  const departureDate = searchData!.dates.departure;

  const passengerCount = useMemo(
    () => searchData!.adult + searchData!.child + searchData!.infantWithoutSeat,
    [searchData],
  );

  const handleDateChange = useCallback(
    (newDepartureDate: Date) => {
      updateSearchData((prevData) => {
        return {
          ...prevData,
          dates: {
            ...prevData?.dates,
            departure: newDepartureDate,
          },
        } as TFlightFormValues;
      });
    },
    [updateSearchData],
  );

  return (
    <>
      <div
        className={clsx(
          "sticky top-0 z-50 flex items-center justify-between gap-2",
          "bg-surface-background py-4 text-text-primary",
          "border-b-0 transition-shadow duration-300 ease-out",
          scrollY > 0
            ? "border-b border-stroke-neutral-soft shadow-sm"
            : "border-transparent shadow-none",
        )}
      >
        <div className="grow overflow-hidden">
          <HeaderSearchButton
            onClick={onSearchDrawerOpen}
            progress={isLoading ? { variant: "indeterminate" } : undefined}
          >
            <div className="flex w-full items-center gap-1.5 text-sm font-medium">
              <h3 className="truncate">{formatLocation(searchData!.from!)}</h3>

              <span className="shrink-0">
                {searchData!.tripType === TripType.ROUND_TRIP ? (
                  <ArrowsLeftRightIcon weight="bold" />
                ) : (
                  <ArrowRightIcon weight="bold" className="rtl:-scale-x-100" />
                )}
              </span>

              <h3 className="truncate">{formatLocation(searchData!.to!)}</h3>
            </div>

            <div className="w-full truncate text-xs font-normal text-text-tertiary">
              {[
                searchData!.tripType === TripType.ONE_WAY
                  ? formatHeaderDate(searchData!.dates.departure)
                  : `${formatHeaderDate(searchData!.dates.departure)} - ${formatHeaderDate(searchData!.dates.return)}`,
                t("numberOfTravellers", { count: passengerCount }),
                t(cabinClassLabels[searchData!.cabinClass]),
              ].map((item, index) => (
                <span key={index}>
                  {index > 0 ? <> &bull; </> : null}
                  {item}
                </span>
              ))}
            </div>
          </HeaderSearchButton>
        </div>
      </div>
      <div className="mb-2 flex gap-1 overflow-auto pt-px pb-2">
        {isLoading ? (
          times(4, (idx) => <FilterButtonSkeleton key={idx} />)
        ) : (
          <>
            <FilterButton
              onClick={() =>
                onFilterButtonClicked(FlightsListDrawerComponent.SORT)
              }
            >
              {t(sortOptionsLabels[selectedSortValue.order])}
              <ArrowsDownUpIcon
                size={20}
                className="fill-text-primary"
                aria-hidden
              />
            </FilterButton>
            <FilterButton
              onClick={() =>
                onFilterButtonClicked(FlightsListDrawerComponent.ALL_FILTERS)
              }
              active={Object.values(activeFilters || {}).some(Boolean)}
            >
              {t("flights.allFilters")}
              <SlidersHorizontalIcon
                size={20}
                className="fill-text-primary"
                aria-hidden
              />
            </FilterButton>
            <FilterButton
              onClick={() =>
                onFilterButtonClicked(FlightsListDrawerComponent.STOPS)
              }
              active={activeFilters?.stops || false}
            >
              {t("flights.stops")}
              <CaretDownIcon
                size={20}
                className="fill-text-primary"
                aria-hidden
              />
            </FilterButton>
            <FilterButton
              onClick={() =>
                onFilterButtonClicked(FlightsListDrawerComponent.AIRLINES)
              }
              active={activeFilters?.selectedAirlines || false}
            >
              {t("flights.airlines")}
              <CaretDownIcon
                size={20}
                className="fill-text-primary"
                aria-hidden
              />
            </FilterButton>
          </>
        )}
      </div>
      <div className="flex flex-col gap-4 bg-surface-background">
        {searchData!.tripType === TripType.ONE_WAY && (
          <CalendarCards
            departureDate={departureDate}
            onDateChange={handleDateChange}
          />
        )}
      </div>
    </>
  );
}

export default Header;
